import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { parlorScreen } from '../../designs';
import OnboardingLayout from './OnboardingLayout';
import { useOnboardingData, clearOnboardingData } from './useOnboardingData';
import { useAuth } from '../../hooks/useAuth';
import { createTrip } from '../../services/trips';
import { trackTripCreated } from '../../config/analytics';
import { getInitials } from './onboardingState';
import Input from '../../components/ui/Input';
import PlacesAutocomplete from '../../components/ui/PlacesAutocomplete';
import DateSpinner from '../../components/ui/DateSpinner';
import TimeSpinner from '../../components/ui/TimeSpinner';
import { TripMember, TripLodging } from '../../types/trip';
import { addTripMember, saveLodging } from '../../services/trips';

const fs = parlorScreen.typography.fontSize;

export default function OnboardingStep3Lodging() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, setData } = useOnboardingData();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleCreateTrip = async () => {
    if (!user) return;
    setIsSubmitting(true);
    setError('');

    try {
      const creatorMember: Omit<TripMember, 'joinedAt'> = {
        uid: user.uid,
        displayName: user.displayName || user.email || 'Planner',
        initials: getInitials(user.displayName || user.email || 'P'),
        role: 'planner',
        isAnonymous: false,
        ...(user.email ? { email: user.email } : {}),
      };

      const tripId = await createTrip(
        user.uid,
        {
          tripName: data.tripName || 'My Trip',
          destination: data.destination,
          startDate: data.startDate,
          endDate: data.endDate,
          ...(data.occasion ? { occasion: data.occasion } : {}),
        },
        creatorMember
      );

      // Add crew members. At most one can be a co-planner (role: 'planner').
      let coPlannerAssigned = false;
      for (const member of data.crew) {
        if (!member.name.trim()) continue;
        const memberUid = `crew_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const isCoPlanner = !!member.isCoPlanner && !coPlannerAssigned;
        if (isCoPlanner) coPlannerAssigned = true;
        const crewMember: TripMember = {
          uid: memberUid,
          displayName: member.name.trim(),
          initials: member.initials,
          role: isCoPlanner ? 'planner' : 'crew',
          isAnonymous: false,
          joinedAt: Date.now(),
        };
        await addTripMember(tripId, memberUid, crewMember);
      }

      // Add lodging only if at least one field has a value
      const hasAnyLodging = data.lodgingName || data.lodgingAddress || data.lodgingAccessCode
        || data.lodgingCheckInDate || data.lodgingCheckInTime
        || data.lodgingCheckOutDate || data.lodgingCheckOutTime || data.lodgingUrl;

      if (hasAnyLodging) {
        const lodging: Partial<TripLodging> = {
          ...(data.lodgingName ? { name: data.lodgingName } : {}),
          ...(data.lodgingAddress ? { address: data.lodgingAddress } : {}),
          ...(data.lodgingAccessCode ? { accessCode: data.lodgingAccessCode } : {}),
          ...(data.lodgingCheckInDate ? { checkInDate: data.lodgingCheckInDate } : {}),
          ...(data.lodgingCheckInTime ? { checkInTime: data.lodgingCheckInTime } : {}),
          ...(data.lodgingCheckOutDate ? { checkOutDate: data.lodgingCheckOutDate } : {}),
          ...(data.lodgingCheckOutTime ? { checkOutTime: data.lodgingCheckOutTime } : {}),
          ...(data.lodgingUrl ? { url: data.lodgingUrl } : {}),
        };
        await saveLodging(tripId, lodging);
      }

      clearOnboardingData();
      trackTripCreated(data.destination, data.crew.length + 1);
      navigate(`/trip/${tripId}`);
    } catch (err) {
      console.error('Failed to create trip:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    await handleCreateTrip();
  };

  return (
    <OnboardingLayout
      step={3}
      headline="Where are you staying?"
      buttonLabel="Start Planning"
      onSubmit={handleCreateTrip}
      onSkip={handleSkip}
      onBack={() => navigate('/new-trip/crew')}
      isSubmitting={isSubmitting}
    >
      <PlacesAutocomplete label="Name" value={data.lodgingName}
        onChange={(val) => setData({ lodgingName: val })}
        onPlaceSelect={(place) => {
          if (!data.lodgingAddress && place.address) setData({ lodgingAddress: place.address });
        }}
      />
      <PlacesAutocomplete label="Address" value={data.lodgingAddress}
        onChange={(val) => setData({ lodgingAddress: val })}
      />
      <Input label="Access Code" type="text" value={data.lodgingAccessCode}
        onChange={(e) => setData({ lodgingAccessCode: e.target.value })} />

      <div style={{ display: 'flex', gap: '16px' }}>
        <DateSpinner label="Check-in Date" value={data.lodgingCheckInDate}
          onChange={(v) => setData({ lodgingCheckInDate: v })} />
        <TimeSpinner label="Check-in Time" value={data.lodgingCheckInTime}
          onChange={(v) => setData({ lodgingCheckInTime: v })} />
      </div>

      <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
        <DateSpinner label="Check-out Date" value={data.lodgingCheckOutDate}
          onChange={(v) => setData({ lodgingCheckOutDate: v })} />
        <TimeSpinner label="Check-out Time" value={data.lodgingCheckOutTime}
          onChange={(v) => setData({ lodgingCheckOutTime: v })} />
      </div>

      <div style={{ marginTop: '16px' }}>
        <Input label="URL" type="text" value={data.lodgingUrl}
          onChange={(e) => setData({ lodgingUrl: e.target.value })} />
      </div>

      {error && (
        <p style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 400,
          color: '#B85C5C', textAlign: 'center', marginTop: '12px',
        }}>
          {error}
        </p>
      )}
    </OnboardingLayout>
  );
}
